"use client"

import * as React from "react"
import { UserProfile } from "@/types/workspace"

export function useSecurityHandlers({
  user,
  setUser,
  showToast
}: {
  user: UserProfile | null
  setUser: React.Dispatch<React.SetStateAction<UserProfile | null>>
  showToast: (title: string, description?: string, variant?: "default" | "destructive" | "success") => void
}) {
  const [setupData, setSetupData] = React.useState<{ secret: string; qrCodeUrl: string } | null>(null)
  const [totpCode, setTotpCode] = React.useState("")
  const [totpDisableCode, setTotpDisableCode] = React.useState("")
  const [totpError, setTotpError] = React.useState<string | null>(null)

  const handleEnable2FA = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) return
    setTotpError(null)

    // Se setupData ainda não foi gerado, busca o QR Code e secret do backend
    if (!setupData) {
      try {
        const res = await fetch("/api/graphql", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            query: `
              mutation Setup2FA {
                setup2FA {
                  secret
                  qrCodeUrl
                }
              }
            `
          })
        })
        const result = await res.json()
        if (result.errors && result.errors.length > 0) {
          setTotpError(result.errors[0].message)
          showToast("Erro ao iniciar 2FA", result.errors[0].message, "destructive")
          return
        }
        if (result.data?.setup2FA) {
          setSetupData(result.data.setup2FA)
        }
      } catch (err) {
        console.error(err)
        setTotpError("Falha ao comunicar com o servidor para configurar 2FA.")
      }
      return
    }

    // Se setupData já existe, valida o código digitado
    if (!totpCode || totpCode.trim().length !== 6) {
      setTotpError("Digite o código de 6 dígitos gerado pelo aplicativo.")
      return
    }

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation Enable2FA($code: String!) {
              enable2FA(code: $code)
            }
          `,
          variables: { code: totpCode.trim() }
        })
      })
      const result = await res.json()
      if (result.errors && result.errors.length > 0) {
        setTotpError(result.errors[0].message)
        showToast("Código Inválido", result.errors[0].message, "destructive")
        return
      }

      if (result.data?.enable2FA) {
        if (user) {
          const updatedUser = { ...user, twoFactorEnabled: true }
          setUser(updatedUser)
          if (typeof localStorage !== "undefined") {
            localStorage.setItem("user", JSON.stringify(updatedUser))
          }
        }
        setSetupData(null)
        setTotpCode("")
        showToast("2FA Ativado", "A autenticação de dois fatores está ativa.", "success")
      }
    } catch (err) {
      console.error(err)
      setTotpError("Falha de rede ao ativar 2FA.")
    }
  }

  const handleDisable2FA = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) return
    setTotpError(null)

    if (!totpDisableCode || totpDisableCode.trim().length !== 6) {
      showToast("Código Inválido", "Digite o código de 6 dígitos do autenticador para desativar.", "destructive")
      return
    }

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation Disable2FA($code: String!) {
              disable2FA(code: $code)
            }
          `,
          variables: { code: totpDisableCode.trim() }
        })
      })
      const result = await res.json()
      if (result.errors && result.errors.length > 0) {
        setTotpError(result.errors[0].message)
        showToast("Erro ao desativar 2FA", result.errors[0].message, "destructive")
        return
      }

      if (result.data?.disable2FA) {
        if (user) {
          const updatedUser = { ...user, twoFactorEnabled: false }
          setUser(updatedUser)
          if (typeof localStorage !== "undefined") {
            localStorage.setItem("user", JSON.stringify(updatedUser))
          }
        }
        setTotpDisableCode("")
        showToast("2FA Desativado", "A autenticação de dois fatores foi desativada.", "success")
      }
    } catch (err) {
      console.error(err)
      setTotpError("Falha de rede ao desativar 2FA.")
    }
  }

  return {
    setupData,
    setSetupData,
    totpCode,
    setTotpCode,
    totpDisableCode,
    setTotpDisableCode,
    totpError,
    setTotpError,
    handleEnable2FA,
    handleDisable2FA
  }
}
